import { createChatStore } from "@sill/shared/chat/createChatStore";
import type {
  ChatDockItem,
  SystemNotice as GenericSystemNotice,
} from "@sill/shared/chat/types";
import { create, type StoreApi, type UseBoundStore } from "zustand";

import type { AdvisorDrawerContext } from "../lib/advisor-context";
import type {
  ClientFrame,
  RenderPayload,
  ServerFrame,
  TextDeltaFrame,
} from "../lib/agent-types";

// Consumer advisor store — now a thin instantiation of the generic
// `createChatStore` (extracted to `frontend/shared/src/chat/createChatStore.ts`,
// plan 188 W2-M8, task 801). The reducer logic, exported shape, and public
// API (`useAdvisorStore`, `isTextDeltaFrame`, `AdvisorDockItem`, ...) are
// byte-identical to the pre-extraction implementation — this file exists so
// consumer keeps its own concrete `RenderPayload`/`AdvisorMessage` types and
// any consumer-only additions (paywall/suggestion specifics live in the
// components that read this store, e.g. `components/advisor/*`, not here).

export type AdvisorServerFrame = ServerFrame;
export type AdvisorClientFrame = ClientFrame;

export function isTextDeltaFrame(
  frame: AdvisorServerFrame,
): frame is TextDeltaFrame {
  return frame.type === "text_delta";
}

export type AdvisorError = {
  code?: string;
  error: string;
};

export type SystemNoticeSeverity = "info" | "warn";
export type SystemNotice = GenericSystemNotice;

export type AdvisorDockItem = ChatDockItem<RenderPayload>;

export const useAdvisorStore: ReturnType<
  typeof createChatStore<RenderPayload>
> = createChatStore<RenderPayload>();

// ─────────────────────────────────────────────────────────────────────────
// Ask Sill drawer chrome (plan 435 M6, task asd-store-context).
//
// Deliberately a SEPARATE store from `useAdvisorStore` above: drawer
// open/closed + the active entity context are frame-level UI chrome
// (decision 528 — "`ConsumerFrame` owns the drawer"), orthogonal to the
// chat SESSION (messages/socket, owned by `AdvisorSessionProvider`). A
// sign-out tears down the chat session AND this drawer state (see
// `AdvisorSessionProvider`'s sign-out effect) — there is deliberately no
// generic `reset()` here; sign-out teardown composes the two existing
// setters (`closeDrawer` + the chat store's `clearMessages`) instead.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Captures whatever had focus immediately before a closed→open transition
 * (focus-return fix, UI review) — `document.activeElement` at the moment
 * `openDrawer` is CALLED, synchronously, before React ever re-renders.
 * This has to happen here rather than in an effect reacting to `drawerOpen`
 * becoming true: by the time such an effect ran, the drawer's own
 * open-autofocus handler (`AdvisorDrawer`'s `onOpenAutoFocus`) would
 * already have stolen focus to the composer textarea, since Radix's
 * `FocusScope` mount-autofocus dispatch runs as a child effect before any
 * sibling/ancestor effect gets a turn. Excludes `<body>` (nothing was
 * really focused — e.g. the `⌘/Ctrl+K` shortcut firing with no focused
 * element) so the restore path can tell "no real opener" apart from "the
 * opener really was the page body." Guards `typeof document ===
 * "undefined"` for store-only unit tests that run in a non-DOM (Node)
 * Vitest environment — this module has no jsdom dependency of its own.
 */
function captureOpenerElement(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body
    ? active
    : null;
}

export interface AdvisorDrawerState {
  /** Question 308 default: persists across in-app navigation; reset on sign-out. */
  drawerOpen: boolean;
  /** Never serialized to the URL or storage — see `lib/advisor-context.ts`. */
  activeContext: AdvisorDrawerContext | null;
  /**
   * The element that had focus immediately before the most recent
   * closed→open transition (focus-return fix, UI review) — set by
   * `openDrawer` itself (see `captureOpenerElement`), read (never
   * subscribed to — this never drives a render) by `AdvisorDrawer`/
   * `AdvisorDrawerPreview`'s `onCloseAutoFocus` to restore focus on an
   * ordinary dismissal (Close button, Escape). Null when nothing
   * meaningful was focused, or stale-but-harmless after the captured
   * element itself unmounts — the restore path checks DOM connectivity
   * before using it. A redundant `openDrawer()` call while already open
   * never re-captures; the original opener still stands.
   */
  lastOpener: HTMLElement | null;
  /**
   * Opens the drawer. An explicit `context` replaces the active one; the
   * common "just open it" call (header pill, sidebar item, `⌘/Ctrl+K`)
   * omits it and leaves whatever context was already set untouched.
   */
  openDrawer: (context?: AdvisorDrawerContext) => void;
  /** Closes the drawer and clears the active context (scenario: "closing
   * the drawer... clears it"). */
  closeDrawer: () => void;
  /**
   * Collapses the drawer WITHOUT clearing `activeContext` (plan 435 M6
   * integration pass). This is the Expand hand-off: the full page's
   * `AdvisorView` resolves `store.activeContext` as its context source
   * (lib/advisor-context.ts `resolveActiveAdvisorContext`'s "store first"
   * precedence) — nulling the context on Expand (as `closeDrawer` does)
   * would erase the very hand-off the design calls for before the full
   * page ever gets to read it. `closeDrawer` remains the right call for
   * every OTHER dismissal (X button, Escape, sign-out) — those really do
   * mean "done with this context", not "handing it to another shell".
   */
  collapseDrawer: () => void;
  setActiveContext: (context: AdvisorDrawerContext | null) => void;
}

export const useAdvisorDrawerStore: UseBoundStore<
  StoreApi<AdvisorDrawerState>
> = create<AdvisorDrawerState>((set) => ({
  drawerOpen: false,
  activeContext: null,
  lastOpener: null,
  openDrawer: (context) =>
    set((state) => ({
      drawerOpen: true,
      activeContext: context ?? state.activeContext,
      lastOpener: state.drawerOpen
        ? state.lastOpener
        : captureOpenerElement(),
    })),
  closeDrawer: () => set({ drawerOpen: false, activeContext: null }),
  collapseDrawer: () => set({ drawerOpen: false }),
  setActiveContext: (context) => set({ activeContext: context }),
}));
